import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import TextInput from '@common/components/Inputs/TextInput';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import { SaveIcon } from '@common/utils/icons/icons';
import { Chip, DialogContent, Stack } from '@mui/material';
import { getCustomerJourneyTagsAsString } from 'models/enums/customer-journey-tag';
import { getExistingMutuallyExclusiveTag, TagType } from 'models/robots/robot-tag';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteTagById, getTags, patchTagById, postTag } from 'store/robots/robots-actions';
import { patchTagsRestSet } from 'store/robots/robots-slice';
import { selectRobotsData, selectTags, selectTagsRest } from 'store/robots/robots-slice-selectors';

const TagsForm: React.FC<{ formType: TagType; onClose: () => void }> = ({ formType, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const tags = useAppSelector((state) => selectTags(state));
  const tagsRest = useAppSelector((state) => selectTagsRest(state));

  useEffect(() => {
    dispatch(patchTagsRestSet({ getStatus: HttpFetchStatus.Idle, getError: null }));
  }, [dispatch]);

  useEffect(() => {
    if (tagsRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getTags());
    }
  }, [dispatch, tagsRest.getStatus]);

  const availableTags = useMemo(() => {
    switch (formType) {
      case TagType.CUSTOMER_JOURNEY:
        return getCustomerJourneyTagsAsString();
      case TagType.CUSTOM:
        return tags?.filter((t) => !getCustomerJourneyTagsAsString().includes(t)) ?? [];
      default:
        break;
    }
    return [];
  }, [formType, tags]);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: tagsRest.patchError,
      resetPatchHandler: () =>
        dispatch(patchTagsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
      postError: tagsRest.postError,
      resetPostHandler: () =>
        dispatch(patchTagsRestSet({ postStatus: HttpFetchStatus.Idle, postError: null })),
    });

  const {
    value: customTag,
    validationResult: customTagValidationResult,
    valueChangeHandler: customTagChangeHandler,
    setValue: setCustomTagHandler,
  } = useValidatedInputEffect(
    '',
    (newTag) =>
      getCustomerJourneyTagsAsString().includes(newTag)
        ? { isValid: false, error: t('validation-error-invalid-tag') }
        : { isValid: true, error: null },
    (_) => () => {},
    getServerValidationResultOrNull('tag'),
    removeServerError,
    resetToggle,
  );

  useEffect(() => {
    if (
      tagsRest.postStatus === HttpFetchStatus.Success ||
      tagsRest.patchStatus === HttpFetchStatus.Success
    ) {
      setCustomTagHandler('');
    }
  }, [setCustomTagHandler, tagsRest.patchStatus, tagsRest.postStatus]);

  const resetTagRestStateHandler = () => {
    dispatch(
      patchTagsRestSet({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const addTagClickHandler = (tag: string) => {
    if (robotsData.currentRobot?.id == null) return;
    const existingPatchTag = getExistingMutuallyExclusiveTag(robotsData.currentRobot.tags, tag);
    if (existingPatchTag != null) {
      dispatch(patchTagById({ id: existingPatchTag.id, data: { tag } }));
    } else {
      dispatch(postTag({ robot: robotsData.currentRobot.id, tag }));
    }
  };

  const saveNewTagHandler = () => {
    if (robotsData.currentRobot?.id == null) return;
    dispatch(postTag({ robot: robotsData.currentRobot.id, tag: customTag }));
  };

  const deleteTagClickHandler = (id: string) => {
    dispatch(deleteTagById(id));
  };

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        open={true}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        title={
          formType === TagType.CUSTOMER_JOURNEY
            ? t('label-customer-success-journey')
            : t('title-robot-tags')
        }
      >
        <DialogContent>
          <Stack spacing={2} pt={2}>
            {formType !== TagType.CUSTOMER_JOURNEY && (
              <TextInput
                label={t('input-label-robot-tag')}
                value={customTag}
                onChange={customTagChangeHandler}
                validationResult={customTagValidationResult}
                endIcon={<SaveIcon />}
                onEndIconClick={saveNewTagHandler}
              />
            )}
            <Stack direction="row" spacing={2}>
              {availableTags
                .filter(
                  (tag) =>
                    customTag.length < 3 || tag.toLowerCase().includes(customTag.toLowerCase()),
                )
                .map((tag) => {
                  const existingTag = robotsData.currentRobot?.tags.find((eT) => eT.tag === tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={existingTag == null ? () => addTagClickHandler(tag) : undefined}
                      color={existingTag != null ? 'info' : undefined}
                      onDelete={
                        existingTag != null
                          ? () => deleteTagClickHandler(existingTag.id)
                          : undefined
                      }
                    />
                  );
                })}
            </Stack>
          </Stack>
        </DialogContent>
      </DialogWrapperWithClose>
      <CustomSnackbar
        title={t('success-saving-tag')}
        open={tagsRest.postStatus === HttpFetchStatus.Success}
        severity="success"
        onClose={resetTagRestStateHandler}
      />
      <CustomSnackbar
        title={t('error-saving-tag')}
        open={tagsRest.postStatus === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetTagRestStateHandler}
      />
      <CustomSnackbar
        title={t('success-saving-tag')}
        open={tagsRest.patchStatus === HttpFetchStatus.Success}
        severity="success"
        onClose={resetTagRestStateHandler}
      />
      <CustomSnackbar
        title={t('error-saving-tag')}
        open={tagsRest.patchStatus === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetTagRestStateHandler}
      />
      <CustomSnackbar
        title={t('success-deleting-tag')}
        open={tagsRest.deleteStatus === HttpFetchStatus.Success}
        severity="success"
        onClose={resetTagRestStateHandler}
      />
      <CustomSnackbar
        title={t('error-deleting-tag')}
        open={tagsRest.deleteStatus === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetTagRestStateHandler}
      />
    </React.Fragment>
  );
};

export default TagsForm;
